/**
 * MANTENIMIENTO — INDICADORES (FASE 12.4)
 *
 * Seguimiento OPERATIVO para gestión (ADMIN, CONSULTOR, ENCARGADO): qué pide atención ahora, qué ha pasado en el
 * periodo, cómo van los preventivos, cuánto se tarda en resolver y dónde se acumula el trabajo. Sin costes, sin
 * rankings de personas, sin SLA inventados: el número, lo que significa y el enlace a los registros que lo forman.
 *
 * Todo sale de `/api/mantenimiento/indicadores/*` (derivado en el servidor con el ámbito ya aplicado). Cada cifra
 * enlazable lleva al listado de partes o incidencias con el MISMO filtro que la produjo, y el periodo, cliente,
 * centro y técnico viven en la URL: «← Volver a Indicadores» deja la pantalla como estaba. `null` = «no hay datos».
 */
import { useId, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { api, type OpcionesActivos } from '../api.ts';
import { Cargando, Error as ErrorCaja, fecha, hoyLocal, useDatos } from '../componentes/base.tsx';
import { num, textoCociente, textoHoras, useDrillMnt } from '../componentes/indicadores-mnt.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useListaUrl } from '../listaUrl.ts';

interface Cociente { pct: number | null; n: number; total: number }
interface Resumen {
  periodo: { desde: string; hasta: string };
  ahora: {
    activos: number; abiertos: number; enCurso: number; pendientes: number; reabiertos: number; atrasados: number;
    sinTecnico: number; porCerrar: number; preventivosVencidos: number; incidenciasAbiertas: number;
  };
  pendientesPorMotivo: { motivo: string; etiqueta: string; total: number; diasMasAntiguo: number | null }[];
  periodoActividad: {
    creados: number; cerrados: number; creadosAnterior: number; cerradosAnterior: number;
    porTipo: { tipo: string; etiqueta: string; total: number }[];
  };
  resolucion: { mediaHoras: number | null; medianaHoras: number | null; n: number; conReapertura: number };
  reaperturas: { eventos: number; partes: number; tasa: Cociente };
  preventivos: { previstos: number; ejecutados: number; vencidos: number; porLlegar: number; anulados: number; cumplimiento: Cociente };
  incidencias: {
    abiertas: number; sinParte: number; conParteActivo: number; porResolver: number; creadas: number; resueltas: number; devueltasARevision: number;
  };
}
interface Mes { mes: string; creados: number; cerrados: number; resolucionMediaHoras: number | null; reaperturas: number; preventivosEjecutados: number }
interface FilaCentro {
  idCentro: string; centro: string; cliente: string; creados: number; creadosAnterior: number; cerrados: number; activos: number;
  atrasados: number; pendientes: number; reabiertos: number; preventivos: Cociente;
}
interface FilaUbicacion {
  idCentro: string; centro: string; idUbicacion: string | null; ubicacion: string; total: number; activos: number; cerrados: number;
  reabiertos: number; correctivos: number; preventivos: number; repetida: boolean;
}
interface FilaActivo { id: string; codigo: string; nombre: string; titularidad: string; centros: string; incidencias: number; correctivos: number; reaperturas: number; repetido: boolean }
interface FilaTecnico {
  idOperario: string | null; nombre: string; activos: number; enCurso: number; pendientes: number; atrasados: number; porCerrar: number;
  cerradosPeriodo: number; minutosProximos7Dias: number;
}
interface PlanPerdidas { id: string; codigo: string; nombre: string; idCentro: string; centro: string; fechas: string[] }

const SIN_DATOS = 'No hay datos suficientes para este periodo.';
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
type Periodo = '30' | '90' | 'anio' | 'personalizado';

/** Fecha LOCAL (nunca la UTC: entre las 00:00 y las 02:00 el periodo acabaría ayer). */
function rango(p: Periodo, desde: string, hasta: string): { desde: string; hasta: string } {
  const hoy = hoyLocal();
  if (p === 'personalizado') return { desde: desde || hoyLocal(-29), hasta: hasta || hoy };
  if (p === 'anio') return { desde: `${hoy.slice(0, 4)}-01-01`, hasta: hoy };
  return { desde: hoyLocal(p === '30' ? -29 : -89), hasta: hoy };
}
function variacion(actual: number, anterior: number): string {
  if (anterior === 0) return actual === 0 ? 'igual que el periodo anterior' : 'sin datos del periodo anterior';
  const v = Math.round(((actual - anterior) * 1000) / anterior) / 10;
  return `${v > 0 ? '+' : ''}${num(v)} % frente al periodo anterior (${String(anterior)})`;
}
const plural = (n: number, uno: string, varios: string): string => `${String(n)} ${n === 1 ? uno : varios}`;

function Tarjeta({ titulo, valor, detalle, a, aviso }: { titulo: string; valor: string; detalle?: string; a?: string; aviso?: boolean }): ReactNode {
  const cuerpo = (
    <>
      <div className="etiqueta-dato">{titulo}</div>
      <div className="cifra" style={{ fontSize: '1.6rem', fontWeight: 600 }}>{valor}</div>
      {detalle !== undefined && <div className="pequena tenue">{detalle}</div>}
    </>
  );
  return (
    <div className={`panel kpi-tarjeta${aviso === true ? ' kpi-aviso' : ''}`} style={{ flex: '1 1 160px', margin: 0 }}>
      <div className="panel-cuerpo">
        {a === undefined ? cuerpo : <Link to={a} className="kpi-enlace" aria-label={`${titulo}: ${valor}. Ver el listado`}>{cuerpo}</Link>}
      </div>
    </div>
  );
}

/** Barras horizontales con su número; cada tramo puede llevar a su listado. */
function Barras({ titulo, tramos, vacio }: { titulo: string; tramos: readonly { clave: string; etiqueta: string; total: number; a?: string; nota?: string }[]; vacio: string }): ReactNode {
  const max = Math.max(1, ...tramos.map((t) => t.total));
  const total = tramos.reduce((s, t) => s + t.total, 0);
  return (
    <div className="panel" style={{ flex: '1 1 280px', margin: 0 }}>
      <div className="panel-cabecera"><h3>{titulo}</h3></div>
      <div className="panel-cuerpo">
        {total === 0 ? <p className="pequena tenue">{vacio}</p> : (
          <ul className="lista-antiguedad" aria-label={titulo}>
            {tramos.map((t) => {
              const fila = (
                <>
                  <span className="pequena">{t.etiqueta}{t.nota !== undefined && <span className="tenue"> · {t.nota}</span>}</span>
                  <span aria-hidden="true" className="barra-fondo"><span className="barra" style={{ width: `${String((t.total / max) * 100)}%` }} /></span>
                  <span className="pequena mono">{t.total}</span>
                </>
              );
              return (
                <li key={t.clave}>
                  {t.a !== undefined && t.total > 0
                    ? <Link to={t.a} className="fila-antiguedad" aria-label={`${t.etiqueta}: ${String(t.total)}`}>{fila}</Link>
                    : <div className="fila-antiguedad">{fila}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export function PaginaIndicadoresMantenimiento(): ReactNode {
  const idBase = useId();
  const l = useLocation();
  const lista = useListaUrl({ periodo: '30', desde: '', hasta: '', idCliente: '', idCentro: '', idOperario: '' });
  const periodo = lista.valor('periodo') as Periodo;
  const idCliente = lista.valor('idCliente');
  const idCentro = lista.valor('idCentro');
  const idOperario = lista.valor('idOperario');
  const r = rango(periodo, lista.valor('desde'), lista.valor('hasta'));
  const q = new URLSearchParams({ desde: r.desde, hasta: r.hasta });
  if (idCliente !== '') q.set('idCliente', idCliente);
  if (idCentro !== '') q.set('idCentro', idCentro);
  if (idOperario !== '') q.set('idOperario', idOperario);
  const consulta = q.toString();

  const opciones = useDatos(() => api.get<OpcionesActivos>('/api/activos/opciones'), []);
  const tecnicos = useDatos(() => api.get<{ filas: { idOperario: string; nombre: string }[] }>(
    `/api/mantenimiento/planner/tecnicos${idCentro === '' ? '' : `?idCentro=${idCentro}`}`), [idCentro]);
  const resumen = useDatos(() => api.get<Resumen>(`/api/mantenimiento/indicadores/resumen?${consulta}`), [consulta]);
  const evolucion = useDatos(() => api.get<{ meses: Mes[] }>(`/api/mantenimiento/indicadores/evolucion?${consulta}`), [consulta]);
  const centros = useDatos(() => api.get<{ filas: FilaCentro[] }>(`/api/mantenimiento/indicadores/centros?${consulta}`), [consulta]);
  const ubicaciones = useDatos(() => api.get<{ filas: FilaUbicacion[] }>(`/api/mantenimiento/indicadores/ubicaciones?${consulta}`), [consulta]);
  const activos = useDatos(() => api.get<{ filas: FilaActivo[] }>(`/api/mantenimiento/indicadores/activos?${consulta}`), [consulta]);
  const carga = useDatos(() => api.get<{ filas: FilaTecnico[] }>(`/api/mantenimiento/indicadores/tecnicos?${consulta}`), [consulta]);
  const perdidos = useDatos(() => api.get<{ filas: PlanPerdidas[] }>(`/api/mantenimiento/indicadores/preventivos-perdidos?${consulta}`), [consulta]);

  const todosCentros = opciones.datos?.centros ?? [];
  const clientes = [...new Map(todosCentros.map((c) => [c.cliente.id, c.cliente.nombre])).entries()];
  const centrosVisibles = todosCentros.filter((c) => idCliente === '' || c.cliente.id === idCliente);
  const nombreTecnico = (tecnicos.datos?.filas ?? []).find((t) => t.idOperario === idOperario)?.nombre ?? '';
  const nombreCliente = clientes.find(([id]) => id === idCliente)?.[1] ?? '';

  //  Lo que acompaña a todo enlace: el ámbito elegido aquí (centro, cliente, técnico) con sus nombres para los avisos.
  const drill = useDrillMnt();
  const ambito: Record<string, string> = {
    centro: idCentro, idCliente, cliente: idCliente === '' ? '' : nombreCliente,
    tecnico: idOperario, tecnicoNombre: idOperario === '' ? '' : nombreTecnico,
  };
  const per = { pDesde: r.desde, pHasta: r.hasta };
  const pt = (filtros: Record<string, string>): string => drill('/mantenimiento/partes', { ...ambito, ...filtros });
  //  Las incidencias no tienen técnico: sólo centro y cliente.
  const im = (filtros: Record<string, string>): string => drill('/mantenimiento/incidencias', { centro: idCentro, idCliente, cliente: ambito['cliente']!, ...filtros });
  const volverAqui = { volver: { ruta: `${l.pathname}${l.search}`, texto: 'Indicadores' } };

  return (
    <Shell migas={[{ texto: 'Mantenimiento' }, { texto: 'Indicadores' }]} titulo="Indicadores de mantenimiento">
      <div className="panel">
        <div className="panel-cuerpo">
          <div className="fila filtros-calidad" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-p`}>Periodo</label>
              <select id={`${idBase}-p`} value={periodo}
                onChange={(e) => lista.fijarVarios(e.target.value === 'personalizado'
                  ? { periodo: 'personalizado', desde: r.desde, hasta: r.hasta } : { periodo: e.target.value, desde: '', hasta: '' })}>
                <option value="30">Últimos 30 días</option>
                <option value="90">Últimos 90 días</option>
                <option value="anio">Año actual</option>
                <option value="personalizado">Personalizado</option>
              </select>
            </div>
            {periodo === 'personalizado' && (
              <>
                <div className="campo" style={{ margin: 0 }}>
                  <label htmlFor={`${idBase}-d`}>Desde</label>
                  <input id={`${idBase}-d`} type="date" value={r.desde} max={r.hasta} onChange={(e) => lista.fijar('desde', e.target.value)} />
                </div>
                <div className="campo" style={{ margin: 0 }}>
                  <label htmlFor={`${idBase}-h`}>Hasta</label>
                  <input id={`${idBase}-h`} type="date" value={r.hasta} min={r.desde} onChange={(e) => lista.fijar('hasta', e.target.value)} />
                </div>
              </>
            )}
            {clientes.length > 1 && (
              <div className="campo" style={{ margin: 0 }}>
                <label htmlFor={`${idBase}-cl`}>Cliente</label>
                <select id={`${idBase}-cl`} value={idCliente} onChange={(e) => lista.fijarVarios({ idCliente: e.target.value, idCentro: '', idOperario: '' })}>
                  <option value="">Todos</option>
                  {clientes.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
                </select>
              </div>
            )}
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-c`}>Centro</label>
              <select id={`${idBase}-c`} value={idCentro} onChange={(e) => lista.fijarVarios({ idCentro: e.target.value, idOperario: '' })}>
                <option value="">Todos</option>
                {centrosVisibles.map((c) => <option key={c.id} value={c.id}>{clientes.length > 1 ? `${c.nombre} · ${c.cliente.nombre}` : c.nombre}</option>)}
              </select>
            </div>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-t`}>Técnico</label>
              <select id={`${idBase}-t`} value={idOperario} onChange={(e) => lista.fijar('idOperario', e.target.value)}>
                <option value="">Todos</option>
                {(tecnicos.datos?.filas ?? []).map((t) => <option key={t.idOperario} value={t.idOperario}>{t.nombre}</option>)}
              </select>
            </div>
          </div>
          <p className="pequena tenue" style={{ marginBottom: 0 }}>
            Del {fecha(r.desde)} al {fecha(r.hasta)}. «Ahora» es el estado actual y no depende del periodo; el resto es lo ocurrido en el periodo,
            con la fecha de cada centro.{idOperario !== '' && ' Con un técnico elegido, las cifras son de sus partes (las incidencias no tienen técnico).'}
          </p>
        </div>
      </div>

      {resumen.cargando && resumen.datos === null && <Cargando texto="Calculando indicadores…" />}
      {resumen.error !== null && <ErrorCaja error={resumen.error} reintentar={resumen.recargar} />}
      {resumen.datos !== null && (
        <>
          <BloqueAtencion r={resumen.datos} pt={pt} im={im} perdidos={perdidos.datos?.filas ?? null} volverAqui={volverAqui} />
          <BloqueActividad r={resumen.datos} pt={pt} per={per} evolucion={evolucion} />
          <BloquePreventivos r={resumen.datos} pt={pt} per={per} />
          <BloqueResolucion r={resumen.datos} pt={pt} per={per} />
          <BloqueIncidencias r={resumen.datos} im={im} per={per} />
        </>
      )}

      <section aria-labelledby="mnt-donde" className="bloque-indicadores">
        <h2 id="mnt-donde" className="titulo-bloque">Dónde se concentra</h2>
        <TablaCentros datos={centros} pt={pt} per={per} />
        <TablaUbicaciones datos={ubicaciones} pt={pt} per={per} />
      </section>
      <TablaActivos datos={activos} pt={pt} per={per} volverAqui={volverAqui} />
      <TablaTecnicos datos={carga} pt={pt} per={per} />
    </Shell>
  );
}

type Enlace = (filtros: Record<string, string>) => string;
type Per = { pDesde: string; pHasta: string };

// ------------------------------------------------------------------ 1 · necesita atención ahora

function BloqueAtencion({ r, pt, im, perdidos, volverAqui }: {
  r: Resumen; pt: Enlace; im: Enlace; perdidos: PlanPerdidas[] | null; volverAqui: { volver: { ruta: string; texto: string } };
}): ReactNode {
  const a = r.ahora;
  const nPerdidas = (perdidos ?? []).reduce((s, p) => s + p.fechas.length, 0);
  return (
    <section aria-labelledby="mnt-ahora" className="bloque-indicadores">
      <h2 id="mnt-ahora" className="titulo-bloque">Necesita atención ahora</h2>
      <div className="fila kpi-rejilla" style={{ gap: 12, flexWrap: 'wrap' }}>
        <Tarjeta titulo="Atrasados" valor={String(a.atrasados)} detalle="programados antes de hoy y sin empezar" a={pt({ indicador: 'ATRASADOS' })} aviso={a.atrasados > 0} />
        <Tarjeta titulo="Sin técnico" valor={String(a.sinTecnico)} detalle="partes activos sin nadie asignado" a={pt({ indicador: 'SIN_TECNICO' })} aviso={a.sinTecnico > 0} />
        <Tarjeta titulo="Pendientes" valor={String(a.pendientes)} detalle="esperando algo para seguir" a={pt({ indicador: 'PENDIENTES' })} />
        <Tarjeta titulo="Finalizados sin cerrar" valor={String(a.porCerrar)} detalle="trabajo hecho; falta el cierre" a={pt({ indicador: 'POR_CERRAR' })} />
        <Tarjeta titulo="Preventivos vencidos" valor={String(a.preventivosVencidos)} detalle="su fecha ya pasó y no están hechos" a={pt({ indicador: 'PREVENTIVOS_VENCIDOS' })} aviso={a.preventivosVencidos > 0} />
        <Tarjeta titulo="Incidencias abiertas" valor={String(a.incidenciasAbiertas)} detalle={`${plural(r.incidencias.sinParte, 'sin parte', 'sin parte')}`} a={im({ indicador: 'IM_ABIERTAS' })} />
      </div>
      <div className="fila" style={{ gap: 12, flexWrap: 'wrap', marginTop: 12, alignItems: 'stretch' }}>
        <div className="panel" style={{ flex: '1 1 280px', margin: 0 }}>
          <div className="panel-cabecera"><h3>Partes activos: {a.activos}</h3></div>
          <div className="panel-cuerpo">
            <ul className="lista-kpi" aria-label="Partes activos ahora">
              <li><span>Sin planificar</span><Link to={pt({ indicador: 'ABIERTOS' })}>{a.abiertos}</Link></li>
              <li><span>En curso</span><Link to={pt({ indicador: 'EN_CURSO' })}>{a.enCurso}</Link></li>
              <li><span>Pendientes</span><Link to={pt({ indicador: 'PENDIENTES' })}>{a.pendientes}</Link></li>
              <li><span>Reabiertos</span><Link to={pt({ indicador: 'REABIERTOS' })}>{a.reabiertos}</Link></li>
              <li><span>Todos los activos</span><Link to={pt({ indicador: 'ACTIVOS' })}>{a.activos}</Link></li>
            </ul>
          </div>
        </div>
        <Barras
          titulo="Pendientes, por motivo"
          vacio="No hay partes pendientes."
          tramos={r.pendientesPorMotivo.map((m) => ({
            clave: m.motivo, etiqueta: m.etiqueta, total: m.total,
            ...(m.diasMasAntiguo === null ? {} : { nota: `el más antiguo, hace ${plural(m.diasMasAntiguo, 'día', 'días')}` }),
            a: pt({ indicador: 'PENDIENTES', motivoPendiente: m.motivo }),
          }))}
        />
      </div>
      {perdidos !== null && perdidos.length > 0 && (
        <div className="panel kpi-aviso" style={{ marginTop: 12 }}>
          <div className="panel-cabecera"><h3>Preventivos que no llegaron a generarse: {nPerdidas}</h3></div>
          <div className="panel-cuerpo">
            <p className="pequena tenue" style={{ marginTop: 0 }}>
              Ocurrencias de planes vigentes que ya pasaron sin parte (como mucho 30 días atrás). No se generan solas hacia atrás: se crean a mano desde el plan.
            </p>
            <ul className="lista-kpi" aria-label="Planes con ocurrencias sin parte">
              {perdidos.map((p) => (
                <li key={p.id}>
                  <span><Link to={`/mantenimiento/preventivos/${p.id}`} state={volverAqui}>{p.codigo} · {p.nombre}</Link> <span className="pequena tenue">{p.centro}</span></span>
                  <span className="mono" title={p.fechas.map(fecha).join(', ')}>{p.fechas.length}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ 2 · actividad del periodo

function BloqueActividad({ r, pt, per, evolucion }: {
  r: Resumen; pt: Enlace; per: Per; evolucion: { datos: { meses: Mes[] } | null; error: unknown; recargar: () => void };
}): ReactNode {
  const a = r.periodoActividad;
  return (
    <section aria-labelledby="mnt-periodo" className="bloque-indicadores">
      <h2 id="mnt-periodo" className="titulo-bloque">Actividad del periodo</h2>
      <div className="fila kpi-rejilla" style={{ gap: 12, flexWrap: 'wrap' }}>
        <Tarjeta titulo="Partes creados" valor={String(a.creados)} detalle={variacion(a.creados, a.creadosAnterior)} a={pt({ indicador: 'CREADOS', ...per })} />
        <Tarjeta titulo="Partes cerrados" valor={String(a.cerrados)} detalle={variacion(a.cerrados, a.cerradosAnterior)} a={pt({ indicador: 'CERRADOS', ...per })} />
      </div>
      <div className="fila" style={{ gap: 12, flexWrap: 'wrap', marginTop: 12, alignItems: 'stretch' }}>
        <Barras
          titulo="Creados, por tipo de trabajo"
          vacio={SIN_DATOS}
          tramos={a.porTipo.map((t) => ({ clave: t.tipo, etiqueta: t.etiqueta, total: t.total, a: pt({ indicador: 'CREADOS', tipo: t.tipo, ...per }) }))}
        />
        <div className="panel" style={{ flex: '2 1 420px', margin: 0 }}>
          <div className="panel-cabecera"><h3>Evolución mensual</h3></div>
          <div className="panel-cuerpo">
            {evolucion.error !== null && <ErrorCaja error={evolucion.error} reintentar={evolucion.recargar} />}
            {evolucion.datos !== null && <TablaEvolucion meses={evolucion.datos.meses} />}
          </div>
        </div>
      </div>
    </section>
  );
}

function TablaEvolucion({ meses }: { meses: readonly Mes[] }): ReactNode {
  if (!meses.some((m) => m.creados + m.cerrados + m.reaperturas + m.preventivosEjecutados > 0)) return <p className="pequena tenue">{SIN_DATOS}</p>;
  const max = Math.max(1, ...meses.map((m) => m.creados));
  return (
    <div className="tabla-desliza">
      <table className="tabla-tarjetas tabla-evolucion">
        <caption className="sr-solo">Evolución por mes</caption>
        <thead><tr><th>Mes</th><th>Creados</th><th>Cerrados</th><th>Tiempo medio de resolución</th><th>Reaperturas</th><th>Preventivos hechos</th></tr></thead>
        <tbody>
          {meses.map((m) => (
            <tr key={m.mes}>
              <td className="celda-titulo">{`${MESES[Number(m.mes.slice(5, 7)) - 1] ?? ''} ${m.mes.slice(0, 4)}`}</td>
              <td data-label="Creados">
                <span className="fila" style={{ gap: 6 }}>
                  <span className="mono">{m.creados}</span>
                  <span aria-hidden="true" style={{ display: 'inline-block', height: 8, width: `${String((m.creados / max) * 80)}px`, background: 'var(--marca, #0f6ea8)', borderRadius: 4 }} />
                </span>
              </td>
              <td data-label="Cerrados">{m.cerrados}</td>
              <td data-label="Tiempo medio de resolución">{textoHoras(m.resolucionMediaHoras)}</td>
              <td data-label="Reaperturas">{m.reaperturas}</td>
              <td data-label="Preventivos hechos">{m.preventivosEjecutados}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------------ 3 · preventivos

function BloquePreventivos({ r, pt, per }: { r: Resumen; pt: Enlace; per: Per }): ReactNode {
  const p = r.preventivos;
  return (
    <section aria-labelledby="mnt-preventivos" className="bloque-indicadores">
      <h2 id="mnt-preventivos" className="titulo-bloque">Preventivos del periodo</h2>
      <div className="fila kpi-rejilla" style={{ gap: 12, flexWrap: 'wrap' }}>
        <Tarjeta titulo="Cumplimiento" valor={textoCociente(p.cumplimiento)}
          detalle={p.cumplimiento.pct === null ? 'todavía no hay preventivos cuya fecha haya pasado' : `${String(p.cumplimiento.n)} hechos de ${String(p.cumplimiento.total)} que ya tocaban`} />
        <Tarjeta titulo="Previstos" valor={String(p.previstos)} detalle="ocurrencias de los planes en el periodo" a={pt({ indicador: 'PREV_PREVISTOS', ...per })} />
        <Tarjeta titulo="Hechos" valor={String(p.ejecutados)} detalle="finalizados o cerrados" a={pt({ indicador: 'PREV_EJECUTADOS', ...per })} />
        <Tarjeta titulo="Vencidos" valor={String(p.vencidos)} detalle="su fecha ya pasó y no están hechos" a={pt({ indicador: 'PREV_VENCIDOS', ...per })} aviso={p.vencidos > 0} />
        <Tarjeta titulo="Por llegar" valor={String(p.porLlegar)} detalle="todavía no toca" a={pt({ indicador: 'PREV_POR_LLEGAR', ...per })} />
      </div>
      <p className="pequena tenue">
        Cumplimiento: de los preventivos del periodo cuya fecha ya llegó, cuántos están hechos. Sin plazos ni tolerancias: no hay un SLA definido.
        {p.anulados > 0 && <> Además, <Link to={pt({ indicador: 'PREV_ANULADOS', ...per })}>{plural(p.anulados, 'anulado', 'anulados')}</Link>, que no cuentan.</>}
      </p>
    </section>
  );
}

// ------------------------------------------------------------------ 4 · resolución y reaperturas

function BloqueResolucion({ r, pt, per }: { r: Resumen; pt: Enlace; per: Per }): ReactNode {
  const res = r.resolucion;
  return (
    <section aria-labelledby="mnt-resolucion" className="bloque-indicadores">
      <h2 id="mnt-resolucion" className="titulo-bloque">Resolución y reaperturas</h2>
      <div className="fila kpi-rejilla" style={{ gap: 12, flexWrap: 'wrap' }}>
        <Tarjeta titulo="Tiempo medio de resolución" valor={textoHoras(res.mediaHoras)}
          detalle={res.n === 0 ? SIN_DATOS : `mediana ${textoHoras(res.medianaHoras)} · ${plural(res.n, 'parte cerrado', 'partes cerrados')}${res.conReapertura > 0 ? `, ${String(res.conReapertura)} tras reabrirse` : ''}`}
          a={res.n === 0 ? undefined : pt({ indicador: 'CERRADOS', ...per })} />
        <Tarjeta titulo="Reaperturas" valor={String(r.reaperturas.eventos)} detalle={plural(r.reaperturas.partes, 'parte distinto', 'partes distintos')}
          a={r.reaperturas.partes === 0 ? undefined : pt({ indicador: 'REABIERTOS_PERIODO', ...per })} />
        <Tarjeta titulo="Tasa de reapertura" valor={textoCociente(r.reaperturas.tasa)}
          detalle={r.reaperturas.tasa.pct === null ? SIN_DATOS : `${String(r.reaperturas.tasa.n)} de ${String(r.reaperturas.tasa.total)} partes cerrados en el periodo`} />
      </div>
      <p className="pequena tenue">
        Resolución: de la creación del parte a su cierre final (si se reabrió, cuenta el último cierre). No es el tiempo de trabajo ni la duración estimada.
        Tasa: de los partes con algún cierre en el periodo, cuántos se reabrieron después.
      </p>
    </section>
  );
}

// ------------------------------------------------------------------ incidencias

function BloqueIncidencias({ r, im, per }: { r: Resumen; im: Enlace; per: Per }): ReactNode {
  const i = r.incidencias;
  return (
    <section aria-labelledby="mnt-incidencias" className="bloque-indicadores">
      <h2 id="mnt-incidencias" className="titulo-bloque">Incidencias</h2>
      <div className="fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <div className="panel" style={{ flex: '1 1 280px', margin: 0 }}>
          <div className="panel-cabecera"><h3>Ahora</h3></div>
          <div className="panel-cuerpo">
            <ul className="lista-kpi" aria-label="Incidencias ahora">
              <li><span>Abiertas o en revisión</span><Link to={im({ indicador: 'IM_ABIERTAS' })}>{i.abiertas}</Link></li>
              <li><span>Sin parte de trabajo</span><Link to={im({ indicador: 'IM_SIN_PT' })}>{i.sinParte}</Link></li>
              <li><span>Con un parte en marcha</span><Link to={im({ indicador: 'IM_CON_PT_ACTIVO' })}>{i.conParteActivo}</Link></li>
              <li><span>Trabajo terminado, por resolver</span><Link to={im({ indicador: 'IM_POR_RESOLVER' })}>{i.porResolver}</Link></li>
            </ul>
          </div>
        </div>
        <div className="panel" style={{ flex: '1 1 280px', margin: 0 }}>
          <div className="panel-cabecera"><h3>En el periodo</h3></div>
          <div className="panel-cuerpo">
            <ul className="lista-kpi" aria-label="Incidencias del periodo">
              <li><span>Registradas</span><Link to={im({ indicador: 'IM_CREADAS', ...per })}>{i.creadas}</Link></li>
              <li><span>Resueltas</span><Link to={im({ indicador: 'IM_RESUELTAS', ...per })}>{i.resueltas}</Link></li>
              <li><span>Devueltas a revisión al reabrirse un parte</span><Link to={im({ indicador: 'IM_DEVUELTAS', ...per })}>{i.devueltasARevision}</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ 5 · dónde

type Datos<T> = { datos: { filas: T[] } | null; error: unknown; recargar: () => void };

function TablaCentros({ datos, pt, per }: { datos: Datos<FilaCentro>; pt: Enlace; per: Per }): ReactNode {
  const filas = datos.datos?.filas ?? [];
  return (
    <div className="panel">
      <div className="panel-cabecera"><h3>Por centro</h3></div>
      <div className="panel-cuerpo">
        <p className="pequena tenue" style={{ marginTop: 0 }}>Creados, cerrados, reabiertos y preventivos: en el periodo. Activos, atrasados y pendientes: ahora. Cada parte cuenta en el centro donde se hizo.</p>
        {datos.error !== null && <ErrorCaja error={datos.error} reintentar={datos.recargar} />}
        {datos.datos !== null && (filas.length === 0 ? <p className="pequena tenue">{SIN_DATOS}</p> : (
          <div className="tabla-desliza">
            <table className="tabla-tarjetas kpi-tabla" aria-label="Partes por centro">
              <thead><tr><th>Centro</th><th>Creados</th><th>Cerrados</th><th>Activos ahora</th><th>Atrasados</th><th>Pendientes</th><th>Reabiertos</th><th>Preventivos hechos</th></tr></thead>
              <tbody>
                {filas.map((c) => {
                  const cen = { centro: c.idCentro };
                  return (
                    <tr key={c.idCentro}>
                      <td className="celda-titulo">{c.centro} <span className="pequena tenue">{c.cliente}</span></td>
                      <td data-label="Creados"><span><Link to={pt({ ...cen, indicador: 'CREADOS', ...per })}>{c.creados}</Link> <span className="pequena tenue">(antes {c.creadosAnterior})</span></span></td>
                      <td data-label="Cerrados"><Link to={pt({ ...cen, indicador: 'CERRADOS', ...per })}>{c.cerrados}</Link></td>
                      <td data-label="Activos ahora"><Link to={pt({ ...cen, indicador: 'ACTIVOS' })}>{c.activos}</Link></td>
                      <td data-label="Atrasados"><Link to={pt({ ...cen, indicador: 'ATRASADOS' })}>{c.atrasados}</Link></td>
                      <td data-label="Pendientes"><Link to={pt({ ...cen, indicador: 'PENDIENTES' })}>{c.pendientes}</Link></td>
                      <td data-label="Reabiertos"><Link to={pt({ ...cen, indicador: 'REABIERTOS_PERIODO', ...per })}>{c.reabiertos}</Link></td>
                      <td data-label="Preventivos hechos">{c.preventivos.pct === null ? '—' : `${textoCociente(c.preventivos)} (${String(c.preventivos.n)} de ${String(c.preventivos.total)})`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

function TablaUbicaciones({ datos, pt, per }: { datos: Datos<FilaUbicacion>; pt: Enlace; per: Per }): ReactNode {
  const filas = datos.datos?.filas ?? [];
  return (
    <div className="panel">
      <div className="panel-cabecera"><h3>Por ubicación</h3></div>
      <div className="panel-cuerpo">
        <p className="pequena tenue" style={{ marginTop: 0 }}>
          Partes creados en el periodo, por la ubicación que tenía su activo cuando se abrió el parte (aunque después se moviera).
          Un parte con activos en varias ubicaciones cuenta en cada una. «Repetida»: dos o más correctivos en el mismo sitio — repetición, no una causa común demostrada.
        </p>
        {datos.error !== null && <ErrorCaja error={datos.error} reintentar={datos.recargar} />}
        {datos.datos !== null && (filas.length === 0 ? <p className="pequena tenue">{SIN_DATOS}</p> : (
          <div className="tabla-desliza">
            <table className="tabla-tarjetas kpi-tabla" aria-label="Partes por ubicación">
              <thead><tr><th>Ubicación</th><th>Centro</th><th>Partes</th><th>Activos ahora</th><th>Cerrados</th><th>Reabiertos</th><th>Correctivos</th><th>Preventivos</th></tr></thead>
              <tbody>
                {filas.map((u) => (
                  <tr key={`${u.idCentro}-${u.idUbicacion ?? 'sin'}`}>
                    <td className="celda-titulo">
                      <Link to={pt({ centro: u.idCentro, indicador: 'CREADOS', ...per, idUbicacion: u.idUbicacion ?? 'SIN', ubicacion: u.ubicacion })}>{u.ubicacion}</Link>
                      {u.repetida && <> <span className="etiqueta aviso">Repetida</span></>}
                    </td>
                    <td data-label="Centro">{u.centro}</td>
                    <td data-label="Partes" className="mono">{u.total}</td>
                    <td data-label="Activos ahora">{u.activos}</td>
                    <td data-label="Cerrados">{u.cerrados}</td>
                    <td data-label="Reabiertos">{u.reabiertos}</td>
                    <td data-label="Correctivos">{u.correctivos}</td>
                    <td data-label="Preventivos">{u.preventivos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ 6 · activos

function TablaActivos({ datos, pt, per, volverAqui }: { datos: Datos<FilaActivo>; pt: Enlace; per: Per; volverAqui: { volver: { ruta: string; texto: string } } }): ReactNode {
  const filas = datos.datos?.filas ?? [];
  return (
    <section aria-labelledby="mnt-activos" className="bloque-indicadores">
      <h2 id="mnt-activos" className="titulo-bloque">Activos con más intervenciones</h2>
      <div className="panel">
        <div className="panel-cuerpo">
          <p className="pequena tenue" style={{ marginTop: 0 }}>
            En el periodo: incidencias registradas sobre el activo, partes correctivos y reaperturas. Es cuánto se ha intervenido, no por qué.
            «Intervenciones repetidas»: dos o más correctivos (no implica la misma avería).
          </p>
          {datos.error !== null && <ErrorCaja error={datos.error} reintentar={datos.recargar} />}
          {datos.datos !== null && (filas.length === 0 ? <p className="pequena tenue">{SIN_DATOS}</p> : (
            <div className="tabla-desliza">
              <table className="tabla-tarjetas kpi-tabla" aria-label="Activos con más intervenciones">
                <thead><tr><th>Activo</th><th>Dónde</th><th>Incidencias</th><th>Correctivos</th><th>Reaperturas</th></tr></thead>
                <tbody>
                  {filas.map((x) => (
                    <tr key={x.id}>
                      <td className="celda-titulo">
                        <Link to={`/activos/${x.id}`} state={volverAqui}><span className="mono">{x.codigo}</span> · {x.nombre}</Link>
                        {x.titularidad === 'EMPRESA' && <> <span className="etiqueta">De la empresa</span></>}
                        {x.repetido && <> <span className="etiqueta aviso">Intervenciones repetidas</span></>}
                      </td>
                      <td data-label="Dónde">{x.centros}</td>
                      <td data-label="Incidencias">{x.incidencias}</td>
                      <td data-label="Correctivos">
                        <Link to={pt({ activo: x.id, activoNombre: `${x.codigo} · ${x.nombre}`, tipo: 'CORRECTIVO', indicador: 'CREADOS', ...per })}>{x.correctivos}</Link>
                      </td>
                      <td data-label="Reaperturas">{x.reaperturas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ 7 · técnicos

function TablaTecnicos({ datos, pt, per }: { datos: Datos<FilaTecnico>; pt: Enlace; per: Per }): ReactNode {
  const filas = datos.datos?.filas ?? [];
  return (
    <section aria-labelledby="mnt-tecnicos" className="bloque-indicadores">
      <h2 id="mnt-tecnicos" className="titulo-bloque">Carga por técnico</h2>
      <div className="panel">
        <div className="panel-cuerpo">
          <p className="pequena tenue" style={{ marginTop: 0 }}>
            Para repartir el trabajo, no para comparar personas: lo que cada uno tiene asignado ahora, en orden alfabético.
            «Próximos 7 días»: la duración estimada de lo que tiene programado.
          </p>
          {datos.error !== null && <ErrorCaja error={datos.error} reintentar={datos.recargar} />}
          {datos.datos !== null && (filas.length === 0 ? <p className="pequena tenue">No hay partes asignados en este ámbito.</p> : (
            <div className="tabla-desliza">
              <table className="tabla-tarjetas kpi-tabla" aria-label="Carga por técnico">
                <thead><tr><th>Técnico</th><th>Activos</th><th>En curso</th><th>Pendientes</th><th>Atrasados</th><th>Sin cerrar</th><th>Cerrados en el periodo</th><th>Próximos 7 días</th></tr></thead>
                <tbody>
                  {filas.map((t) => {
                    const quien: Record<string, string> = t.idOperario === null ? { indicador: 'SIN_TECNICO' } : { tecnico: t.idOperario, tecnicoNombre: t.nombre };
                    const enlace = (indicador: string, extra: Record<string, string> = {}): string => pt({ ...quien, indicador, ...extra });
                    return (
                      <tr key={t.idOperario ?? 'sin'}>
                        <td className="celda-titulo">{t.nombre}</td>
                        <td data-label="Activos"><Link to={t.idOperario === null ? pt({ indicador: 'SIN_TECNICO' }) : enlace('ACTIVOS')}>{t.activos}</Link></td>
                        <td data-label="En curso">{t.idOperario === null ? t.enCurso : <Link to={enlace('EN_CURSO')}>{t.enCurso}</Link>}</td>
                        <td data-label="Pendientes">{t.idOperario === null ? t.pendientes : <Link to={enlace('PENDIENTES')}>{t.pendientes}</Link>}</td>
                        <td data-label="Atrasados">{t.idOperario === null ? t.atrasados : <Link to={enlace('ATRASADOS')}>{t.atrasados}</Link>}</td>
                        <td data-label="Sin cerrar">{t.idOperario === null ? t.porCerrar : <Link to={enlace('POR_CERRAR')}>{t.porCerrar}</Link>}</td>
                        <td data-label="Cerrados en el periodo">{t.idOperario === null ? t.cerradosPeriodo : <Link to={enlace('CERRADOS', per)}>{t.cerradosPeriodo}</Link>}</td>
                        <td data-label="Próximos 7 días">{t.minutosProximos7Dias === 0 ? '—' : textoHoras(t.minutosProximos7Dias / 60)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
